// TEMP — Eruda mobile debugger. Activated only when ?debug=true is in
// the URL so normal users never load it. Remove this block once the
// read-aloud issue is resolved.
if (
  typeof window !== 'undefined' &&
  window.location.search.includes('debug=true')
) {
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/eruda';
  script.onload = () => window.eruda && window.eruda.init();
  document.head.appendChild(script);
}

import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './index.css'
import App from './App.jsx'
import SentryUserBridge from './SentryUserBridge.jsx';
import { setApiTokenGetter } from './services/apiClient.js'

// Lives inside <ClerkProvider> so it can call useAuth(). On mount it
// hands the apiClient module a function that resolves to a fresh
// session token, which authenticatedFetch then attaches to every
// /api/* request as Authorization: Bearer.
function ClerkAuthBridge({ children }) {
  const { getToken } = useAuth();

  useEffect(() => {
    setApiTokenGetter(() => getToken());
    return () => setApiTokenGetter(null);
  }, [getToken]);

  return children;
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Filter out noisy expected errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend(event, hint) {
      // Drop events that lack a stack trace (usually browser noise)
      if (!event.exception?.values?.[0]?.stacktrace) return null;
      return event;
    },
  });
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  console.warn('Clerk publishable key not found');
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div
          style={{
            padding: 40,
            fontFamily: 'Georgia, serif',
            textAlign: 'center',
          }}
        >
          Something went wrong. Please refresh the page.
        </div>
      }
    >
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/study"
        signUpFallbackRedirectUrl="/enneagram"
      >
        <ClerkAuthBridge>
          <SentryUserBridge>
            <App />
            <Analytics />
            <SpeedInsights />
          </SentryUserBridge>
        </ClerkAuthBridge>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
